import * as React from "react";
import FormatDecimal from "../FormatDecimal";
import { useEffect, useState } from "react";
import { Timespan, TimespanPager } from "./TimespanPager";
import {
  VizDTO,
  GroupingType,
  OperationType,
  IVizDTO,
  useGroopitApiContext,
} from "groopit-js";
import { getGroupingTypes } from "../VizTypeChooser";

interface CounterProps {
  Counter: number;
  TotalCount?: number;
  Format: string;
  WeeklyDelta: number;
  Label?: string;
  Grouping: GroupingType;
  Operation: OperationType;
}

function groupingName(grouping: GroupingType) {
  switch (grouping) {
    case GroupingType.Daily:
      return "Day";
    case GroupingType.Weekly:
      return "Week";
    case GroupingType.Monthly:
      return "Month";
    case GroupingType.Quarterly:
      return "Quarter";
    case GroupingType.Annually:
      return "Year";
    case GroupingType.None:
      return "All";
  }
}

export const CounterDelta = (props: CounterProps): JSX.Element => {
  const {
    Counter,
    TotalCount,
    WeeklyDelta,
    Label,
    Format,
    Grouping,
    Operation,
  } = {
    ...props,
  };
  //
  // Compute the correct font size so the number doesn't
  // exceed the allocated size.
  //
  const formatted = FormatDecimal(Counter, Format);
  const totalFormatted = TotalCount
    ? FormatDecimal(TotalCount, Format)
    : undefined;

  let fontSize = "72px";
  let totalFontSize = "28px";
  if (formatted.length >= 9) {
    fontSize = "24px";
    totalFontSize = "14px";
  } else if (formatted.length >= 6) {
    fontSize = "36px";
    totalFontSize = "20px";
  }

  return (
    <div className="flex items-center text-center">
      {Operation != OperationType.Mean ? (
        <>
          <div className="flex-1 counter text-color-accent">
            <div
              className="counter-number"
              style={{ fontSize, lineHeight: totalFontSize ? 1 : undefined }}
            >
              {formatted}
            </div>
            {totalFormatted && (
              <div
                className="counter-total"
                style={{ fontSize: totalFontSize }}
              >
                OF {totalFormatted}
              </div>
            )}
            <div className="counter-subtitle text-uppercase">{Label}</div>
          </div>
          <div className="flex-1 counter-delta text-color-primary">
            <div className="counter-trend text-6xl">
              {WeeklyDelta > 0 ? (
                <span className="glyphicon glyphicon-arrow-up"></span>
              ) : (
                <span className="glyphicon glyphicon-minus"></span>
              )}
            </div>
            <div className="text-uppercase">
              {WeeklyDelta > 0 ? "Up " : ""}
              {FormatDecimal(WeeklyDelta, Format)} this {groupingName(Grouping)}
            </div>
          </div>
        </>
      ) : (
        <div className="counter m-auto text-color-accent">
          <div className="counter-number" style={{ fontSize }}>
            {FormatDecimal(Counter, Format)}
          </div>
          <div className="counter-subtitle text-uppercase">{Label}</div>
        </div>
      )}
    </div>
  );
};

type CounterVizProps = Omit<IVizDTO, "title" | "descripton">;

export const CounterViz = (props: CounterVizProps) => {
  const { grouping, operation, label } = props;
  const { viz } = useGroopitApiContext();
  const [counter, setCounter] = useState<CounterProps>();

  useEffect(() => {
    const loadData = async () => {
      const json = await viz.getDataPOST(new VizDTO({ title: "", ...props }));
      setCounter(json);
    };

    loadData();
  }, [props]);
  if (!counter) {
    return <div>Loading...</div>;
  } else {
    return (
      <CounterDelta
        {...counter}
        Grouping={grouping}
        Operation={operation}
        Label={label}
      ></CounterDelta>
    );
  }
};

export const CounterVizWrapper = (props: CounterVizProps): JSX.Element => {
  const [timespan, setTimespan] = useState(
    new Timespan(props.endDate, props.grouping, props.startDate, props.endDate),
  );
  useEffect(() => {
    if (
      props.startDate != timespan.startDate ||
      props.endDate != timespan.endDate ||
      props.grouping != timespan.grouping
    ) {
      setTimespan(
        new Timespan(
          timespan.date,
          props.grouping,
          props.startDate,
          props.endDate,
        ),
      );
    }
  }, [props.startDate, props.endDate, props.grouping]);

  return (
    <>
      {getGroupingTypes(props.vizType, props.operation).filter(
        (g) => g != GroupingType.None,
      ).length > 0 && (
        <TimespanPager
          timespan={timespan}
          on_change={setTimespan}
          groupings={getGroupingTypes(props.vizType, props.operation)}
        />
      )}
      <CounterViz
        {...props}
        grouping={timespan.grouping}
        endDate={timespan.end}
      />
    </>
  );
};
